import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { getV2TenantGroups } from '@/api/white-labels';
import { getSchoolOptions } from '@/api/schools';
import { deployV2, getV2Progress } from '@/api/v2-deployment';
import { V2DeploymentStatus } from './V2DeploymentStatus';
import { V2SubscriptionForm, type V2SubscriptionFormData } from './V2SubscriptionForm';
import type { V2TenantGroup, V2DeployRequest, V2SetupStatus, V2DeploymentInfo } from '@/types/v2-deployment';
import type { SchoolOptions } from '@/types/school';
import { Loader2, Server, AlertTriangle } from 'lucide-react';

interface V2DeployDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schoolCode: string;
  schoolName: string;
  onSuccess?: () => void;
  /** Optional config to deploy immediately without showing form */
  deployConfig?: V2DeployRequest;
}

type DialogStep = 'loading' | 'form' | 'deploying' | 'progress';

export function V2DeployDialog({
  open,
  onOpenChange,
  schoolCode,
  schoolName,
  onSuccess,
  deployConfig,
}: V2DeployDialogProps) {
  const [loading, setLoading] = useState(true);
  const [deploying, setDeploying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<DialogStep>('loading');
  const [setupStatus, setSetupStatus] = useState<V2SetupStatus | null>(null);
  const [existingDeployment, setExistingDeployment] = useState<V2DeploymentInfo | null>(null);
  const deployTriggeredRef = useRef(false);

  // Form data
  const [tenantGroups, setTenantGroups] = useState<V2TenantGroup[]>([]);
  const [defaultTenantGroupId, setDefaultTenantGroupId] = useState<string>('');
  const [schoolOptions, setSchoolOptions] = useState<SchoolOptions | null>(null);
  const [formData, setFormData] = useState<V2SubscriptionFormData>({
    tenantGroupId: '',
    offerId: null,
    optionIds: [],
  });

  // Auto-deploy function
  const triggerDeploy = async (config: V2DeployRequest) => {
    if (deployTriggeredRef.current) return;
    deployTriggeredRef.current = true;

    setStep('deploying');
    setError(null);

    try {
      await deployV2(schoolCode, config);
      setStep('progress');
    } catch (err) {
      setError((err as Error).message || 'Erreur lors du deploiement');
      setStep('form');
      deployTriggeredRef.current = false;
    }
  };

  // Check existing deployment and load data when dialog opens
  useEffect(() => {
    if (open) {
      setStep('loading');
      setError(null);
      setSetupStatus(null);
      setLoading(true);
      setExistingDeployment(null);
      deployTriggeredRef.current = false;

      // Load all required data
      Promise.all([
        getV2Progress(schoolCode).catch(() => null),
        getV2TenantGroups(),
        getSchoolOptions(),
      ])
        .then(([deploymentInfo, groupsResult, optionsResult]) => {
          // Set tenant groups
          setTenantGroups(groupsResult.data);
          const defaultId = groupsResult.defaultGroupId || groupsResult.data[0]?.id || '';
          setDefaultTenantGroupId(defaultId);

          // Set school options
          setSchoolOptions(optionsResult);

          // Initialize form with default values
          setFormData({
            tenantGroupId: defaultId,
            offerId: deployConfig?.offerId || null,
            optionIds: deployConfig?.optionIds || [],
          });

          if (groupsResult.warning) {
            setError(groupsResult.warning);
          }

          // Check if already deployed
          if (deploymentInfo?.deployed && deploymentInfo?.tenantId) {
            setExistingDeployment(deploymentInfo);
            setSetupStatus(deploymentInfo.progress?.status || deploymentInfo.localStatus || null);
            setStep('progress');
          } else if (deployConfig) {
            // Auto-deploy with provided config
            const config: V2DeployRequest = {
              tenantGroupId: deployConfig.tenantGroupId || defaultId,
              ...deployConfig,
            };
            triggerDeploy(config);
          } else {
            setStep('form');
          }
        })
        .catch((err) => {
          setError((err as Error).message || 'Erreur lors du chargement');
          setStep('form');
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [open, schoolCode, deployConfig]);

  const handleDeploy = async () => {
    setDeploying(true);
    setError(null);
    setStep('deploying');

    try {
      const deployData: V2DeployRequest = {
        tenantGroupId: formData.tenantGroupId || undefined,
        offerId: formData.offerId || undefined,
        optionIds: formData.optionIds.length > 0 ? formData.optionIds : undefined,
      };

      await deployV2(schoolCode, deployData);
      setStep('progress');
    } catch (err) {
      setError((err as Error).message || 'Erreur lors du deploiement');
      setStep('form');
    } finally {
      setDeploying(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    // Call onSuccess if deployment was started or changed
    if (step === 'progress' || existingDeployment) {
      onSuccess?.();
    }
  };

  const handleStatusChange = (status: V2SetupStatus | null) => {
    setSetupStatus(status);
  };

  const getDialogTitle = () => {
    if (step === 'deploying') return 'Deploiement V2 en cours';
    if (step === 'progress') {
      if (setupStatus === 'failed') return 'Deploiement V2 echoue';
      if (setupStatus === 'completed') return 'Deploiement V2 termine';
      if (setupStatus === 'in_progress') return 'Deploiement V2 en cours';
      return 'Statut du deploiement V2';
    }
    return 'Deployer en V2';
  };

  const getDialogDescription = () => {
    if (step === 'deploying') {
      return (
        <>
          Deploiement de <strong>{schoolName}</strong> en cours...
        </>
      );
    }
    if (step === 'progress') {
      if (setupStatus === 'failed') {
        return (
          <>
            Le deploiement de <strong>{schoolName}</strong> a echoue. Vous pouvez relancer ou reinitialiser.
          </>
        );
      }
      return (
        <>
          Suivi du deploiement de <strong>{schoolName}</strong>
        </>
      );
    }
    return (
      <>
        Déployer l'école <strong>{schoolName}</strong> sur l'infrastructure V2
      </>
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Server className="h-5 w-5" />
            {getDialogTitle()}
          </DialogTitle>
          <DialogDescription>{getDialogDescription()}</DialogDescription>
        </DialogHeader>

        {/* Loading state */}
        {step === 'loading' && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            <span className="ml-2 text-gray-500">Chargement...</span>
          </div>
        )}

        {/* Deploying state - show immediately when deploy starts */}
        {step === 'deploying' && (
          <div className="py-4">
            <V2DeploymentStatus
              schoolCode={schoolCode}
              onStatusChange={handleStatusChange}
              autoRefresh={true}
              refreshInterval={1000}
            />
          </div>
        )}

        {/* Form step */}
        {step === 'form' && (
          <div className="space-y-4">
            <V2SubscriptionForm
              options={schoolOptions}
              tenantGroups={tenantGroups}
              defaultTenantGroupId={defaultTenantGroupId}
              value={formData}
              onChange={setFormData}
              showTenantGroup={true}
            />

            {/* Error message */}
            {error && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </div>
        )}

        {/* Progress step - show deployment status */}
        {step === 'progress' && (
          <div className="py-4">
            <V2DeploymentStatus
              schoolCode={schoolCode}
              onStatusChange={handleStatusChange}
              autoRefresh={true}
              refreshInterval={1000}
            />

            {/* Error message */}
            {error && (
              <Alert variant="destructive" className="mt-4">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </div>
        )}

        {/* Footer */}
        <DialogFooter className="flex-col sm:flex-row gap-2">
          {step === 'form' && (
            <>
              <Button variant="outline" onClick={handleClose} disabled={deploying}>
                Annuler
              </Button>
              <Button onClick={handleDeploy} disabled={loading || deploying || tenantGroups.length === 0}>
                {deploying ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Deploiement...
                  </>
                ) : (
                  <>
                    <Server className="h-4 w-4 mr-2" />
                    Deployer
                  </>
                )}
              </Button>
            </>
          )}
          {(step === 'deploying' || step === 'progress') && (
            <Button variant="outline" onClick={handleClose}>
              {setupStatus === 'completed' ? 'Termine' : 'Fermer'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
